// ===========================================
// Term lock (frontend mirror)
// ===========================================
// A term LOCKS when the NEXT term starts (plan md/plan/term-lock-at-next-term-start.md).
// This is the FE mirror of backend `services/termLock.service.ts`; both read the shared case
// table `apps/backend/tests/fixtures/termLockCases.json` (see termLock.agreement.test.ts).
//
//   successor of T = the earliest ACTIVE term, other than T, whose startDate is AFTER T's END date
//                    (tie: lowest id).
//   LOCKED         = a successor exists, it has started on the SCHOOL's day (`schoolToday`, a UTC
//                    midnight from `schoolTodayUtcMidnight`, NEVER `startOfTodayUTC()`), and no admin
//                    reopen is live (`reopenedUntil > now`, real instants, strictly greater).
//   REOPENED       = would be locked, but a reopen is live.
//   OPEN           = everything else (incl. an ended term with no successor yet).
//
// `isTermEnded` (lessons.helpers) keeps meaning "the term has finished" and is never an edit gate.

import { formatUTCDate, formatInSchoolTz } from './dateUtils';
import { apiErrorCode } from './apiError';

export interface LockableTermLike {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  reopenedUntil?: string | null;
}

export type TermLockState<T extends LockableTermLike = LockableTermLike> =
  | { status: 'OPEN'; ended: boolean; successor: T | null }
  | { status: 'LOCKED'; successor: T }
  | { status: 'REOPENED'; successor: T; reopenedUntil: Date };

const ms = (iso: string): number => new Date(iso).getTime();

/** The earliest ACTIVE term starting after `term`'s END date; tie broken by id. */
export function findSuccessorTerm<T extends LockableTermLike>(
  term: Pick<T, 'id' | 'endDate'>,
  terms: readonly T[]
): T | null {
  const end = ms(term.endDate);
  let best: T | null = null;
  for (const s of terms) {
    if (s.id === term.id || !s.isActive || !(ms(s.startDate) > end)) continue;
    if (
      best === null ||
      ms(s.startDate) < ms(best.startDate) ||
      (ms(s.startDate) === ms(best.startDate) && s.id < best.id)
    ) {
      best = s;
    }
  }
  return best;
}

/**
 * Lock state of `term`. `schoolToday` is the SCHOOL's today as a UTC midnight
 * (`schoolTodayUtcMidnight(tz)`); `now` is the real instant, for the reopen expiry only.
 */
export function termLockState<T extends LockableTermLike>(
  term: T,
  terms: readonly T[],
  schoolToday: Date,
  now: Date = new Date()
): TermLockState<T> {
  const successor = findSuccessorTerm<T>(term, terms);
  const ended = ms(term.endDate) < schoolToday.getTime();
  if (!successor || ms(successor.startDate) > schoolToday.getTime()) {
    return { status: 'OPEN', ended, successor };
  }
  if (term.reopenedUntil) {
    const until = new Date(term.reopenedUntil);
    if (until.getTime() > now.getTime()) {
      return { status: 'REOPENED', successor, reopenedUntil: until };
    }
  }
  return { status: 'LOCKED', successor };
}

export function isTermLocked<T extends LockableTermLike>(
  term: T,
  terms: readonly T[],
  schoolToday: Date,
  now: Date = new Date()
): boolean {
  return termLockState(term, terms, schoolToday, now).status === 'LOCKED';
}

const dmy = (iso: string): string =>
  formatUTCDate(iso, { day: '2-digit', month: '2-digit', year: 'numeric' }, 'en-AU');

/** "Term 3 is locked because Term 4 started on 12/10/2026." (empty when not locked). */
export function termLockedReason(
  term: Pick<LockableTermLike, 'name'>,
  state: TermLockState
): string {
  if (state.status !== 'LOCKED') return '';
  return `${term.name} is locked because ${state.successor.name} started on ${dmy(state.successor.startDate)}.`;
}

/**
 * Terms an admin can pre-book into: active and not LOCKED (so a finished-but-open term stays
 * offered through the holidays), soonest first.
 */
export function getPreBookableTerms<T extends { isActive: boolean; startDate: string }>(
  terms: T[] | undefined,
  isLocked: (t: T) => boolean
): T[] {
  return (terms ?? [])
    .filter((t) => t.isActive && !isLocked(t))
    .sort((a, b) => ms(a.startDate) - ms(b.startDate));
}

/** True when an API error is the server's `TERM_LOCKED` refusal (`details.code`). */
export function isTermLockedError(err: unknown): boolean {
  return apiErrorCode(err) === 'TERM_LOCKED';
}

/**
 * "dd/mm/yyyy" of the LAST school day a reopen covers. `reopenedUntil` is the instant the school
 * day AFTER that day begins, so the covered day is one minute earlier, read in the school's zone.
 */
export function reopenLastDayLabel(reopenedUntil: string | Date, timeZone: string): string {
  const d = typeof reopenedUntil === 'string' ? new Date(reopenedUntil) : reopenedUntil;
  return formatInSchoolTz(new Date(d.getTime() - 60_000), timeZone, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/** The school day `daysFromToday` after `schoolToday`, as dd/mm/yyyy. */
export function schoolDayLabel(schoolToday: Date, daysFromToday: number): string {
  return formatUTCDate(
    new Date(schoolToday.getTime() + daysFromToday * 86_400_000),
    { day: '2-digit', month: '2-digit', year: 'numeric' },
    'en-AU'
  );
}
